import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Dismissible "Day N of the 30-day tutorial" card for role landings.
 * Persists progress per-user via /api/tutorial/progress.
 * Hidden entirely for CLIENT role (endpoint returns skip:true).
 */
export default function TutorialDayCard({ compact = false }) {
    const { user } = useAuth();
    const [card, setCard] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/tutorial/today?lang=${user?.language || "en"}`)
            .then((r) => setCard(r.data))
            .catch(() => setCard(null));
    }, [user?.language]);

    async function mark(status) {
        if (!card || card.skip) return;
        setBusy(true);
        try {
            await api.post("/tutorial/progress", { day: card.day, status });
            setCard((c) => ({ ...c, status }));
        } finally { setBusy(false); }
    }

    if (!card || card.skip) return null;
    if (card.status === "dismissed" || card.status === "done") return null;

    return (
        <div
            className={`rounded-xl border border-[#1E3355]/15 bg-gradient-to-br from-[#eef2f9] to-white p-4 sm:p-5 mb-4 sm:mb-6 shadow-sm relative`}
            data-testid="tutorial-day-card"
        >
            <button
                onClick={() => mark("dismissed")}
                className="absolute top-2 right-2 p-1 rounded hover:bg-black/5 text-gray-500"
                aria-label="Dismiss tutorial card"
                data-testid="tutorial-dismiss"
            >
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#E02B2B] text-white flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1 pr-6">
                    <div className="text-[11px] uppercase tracking-wider text-[#1E3355]/60 font-semibold">
                        Day {card.day} of your 30-day tutorial · {card.track === "admin" ? "Admin track" : "Field track"}
                    </div>
                    <div className="text-[15px] sm:text-base font-semibold text-[#1E3355] mt-0.5">
                        {card.title}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{card.body}</div>
                    <div className="mt-3 flex items-center gap-2">
                        {card.link_to && (
                            <Link
                                to={card.link_to}
                                onClick={() => mark("seen")}
                                className="inline-flex items-center gap-1 rounded-full bg-[#1E3355] text-white text-xs px-3 py-1.5 hover:bg-[#15243C]"
                                data-testid="tutorial-open-link"
                            >
                                Open now →
                            </Link>
                        )}
                        <button
                            onClick={() => mark("done")}
                            disabled={busy}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-600 text-emerald-700 text-xs px-3 py-1.5 hover:bg-emerald-50"
                            data-testid="tutorial-mark-done"
                        >
                            <Check className="w-3 h-3" /> Mark done
                        </button>
                        <Link
                            to="/tutorial"
                            className="text-xs text-[#1E3355]/70 hover:text-[#1E3355] underline ml-auto"
                            data-testid="tutorial-view-all"
                        >
                            View all 30 days
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
